import NiotaClassicApiService from '../../../services/niotaClassicApiService';
import debounce from 'lodash/debounce';
import listMixin from '../listMixin';
import { mapState } from 'vuex';
import get from 'lodash/get';
import { userItemsPerListMixin } from '../../../utils/vuex';
const defaultItemsPerPage = 10;
const defaultTableOptions = {
  sortBy: ['id'],
  sortDesc: [false],
  page: 1,
  itemsPerPage: defaultItemsPerPage
};
export default {
  name: 'NiotaClassicApplicationsList',
  mixins: [listMixin, userItemsPerListMixin],
  props: {
    selectedAccounts: {
      type: Array,
      required: false,
      default: undefined
    }
  },
  data: function () {
    return {
      lastSearch: '',
      isInitialSearch: false,
      disableFetchOnCreatedMixin: true,
      tableOptions: {
        ...defaultTableOptions
      }
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme', 'permissions']),
    tableLocalizations() {
      return {
        headers: [{
          text: this.$i18n_t('NiotaClassic.Common.table.id'),
          value: 'id',
          align: 'right'
        }, {
          sortable: false
        }, {
          text: this.$i18n_t('NiotaClassic.Common.table.title'),
          value: 'title'
        }, {
          text: this.$i18n_t('NiotaClassic.Applications.table.devices'),
          sortable: false,
          value: 'active'
        }, {
          text: this.$i18n_t('NiotaClassic.Applications.table.status'),
          sortable: false,
          value: 'active'
        }, {
          text: this.$i18n_t('NiotaClassic.Applications.table.account'),
          sortable: false,
          value: 'accountName1'
        }, {
          text: this.$i18n_t('NiotaClassic.Common.table.createdAt'),
          value: 'createdAt'
        }, ...(this.isIotHubManager ? [{
          sortable: false
        }] : [])],
        tableFooterProps: {
          itemsPerPageText: this.$i18n_t('Common.rowsPerPage'),
          itemsPerPageOptions: [10, defaultItemsPerPage, 20, 50]
        }
      };
    }
  },
  watch: {
    selectedAccounts: debounce(function () {
      // Items have already been requested
      if (this.isFetching) {
        return;
      }
      this.updateAccountParams();
      this.fetchList(true);
    }, 500)
  },
  created() {
    defaultTableOptions.itemsPerPage = this.savedUserItemsPerList();
    const page = parseInt(this.$route.query.page, 10) || defaultTableOptions.page;
    const perPage = parseInt(this.$route.query.perPage, 10) || defaultTableOptions.itemsPerPage;
    const search = this.$route.query.search || '';
    if (search) {
      this.isInitialSearch = true;
      this.searchItem = search;
    }
    this.tableOptions.page = page;
    this.tableOptions.itemsPerPage = perPage;
  },
  methods: {
    updatePagination(e) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          ...{
            page: e.page || defaultTableOptions.page
          },
          ...{
            perPage: e.itemsPerPage || defaultTableOptions.itemsPerPage
          }
        }
      }).catch(error => {
        console.error(error);
      });
    },
    updateAccountParams() {
      this.$router.replace({
        query: {
          ...this.$route.query,
          ...{
            accounts: this.getAccountIds(this.selectedAccounts).join(',') || undefined
          }
        }
      }).catch(error => {
        console.error(error);
      });
    },
    updateSearchParams(searchTerm) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          ...{
            search: searchTerm || undefined
          } // set to undefined on empty string to remove from query params
        }
      }).catch(error => {
        console.error(error);
      });
    },
    fetchList(fromStart = false) {
      if (this.isFetching) {
        return;
      }
      this.isFetching = true;
      const trimmedSearch = (this.searchItem || '').trim();
      if (this.lastSearch !== trimmedSearch) {
        this.lastSearch = trimmedSearch;
        if (!this.isInitialSearch) {
          this.tableOptions = {
            ...this.tableOptions,
            page: 1
          };
        }
        this.isInitialSearch = false;
      }
      const tableOptions = fromStart ? {
        ...this.tableOptions,
        page: 1
      } : this.tableOptions;
      const {
        sortBy,
        sortDesc,
        page,
        itemsPerPage
      } = tableOptions;
      const sortItem = sortBy && sortBy.length ? sortBy[0] : 'createdAt';
      const doSortDesc = sortDesc && sortDesc.length ? sortDesc[0] : true;
      const sortDirection = doSortDesc ? 'DESC' : 'ASC';
      const usedPage = Math.max(1, page || 1);
      const usedItemsPerPage = itemsPerPage || defaultItemsPerPage;
      let promise = undefined;
      if (trimmedSearch) {
        this.updateSearchParams(trimmedSearch);
        if (!this.selectedAccounts.length) {
          promise = NiotaClassicApiService.applicationsFind(trimmedSearch, this.getAccountIds(this.selectedAccounts), usedPage, usedItemsPerPage, sortItem, sortDirection);
        } else {
          promise = NiotaClassicApiService.applicationsFindByAccount(this.getAccountIds(this.selectedAccounts), trimmedSearch, usedPage, usedItemsPerPage, sortItem, sortDirection);
        }
      } else {
        this.updateSearchParams('');
        if (!this.selectedAccounts.length) {
          promise = NiotaClassicApiService.applicationsFetch(usedPage, usedItemsPerPage, sortItem, sortDirection);
        } else {
          promise = NiotaClassicApiService.applicationsFetchByAccount(this.getAccountIds(this.selectedAccounts), usedPage, usedItemsPerPage, sortItem, sortDirection);
        }
      }
      promise.then(res => {
        this.list = res.data.applications || res.data.application;
        this.serverItemsLength = res.pagination.itemCount;
      }).catch(error => {
        console.error('Error fetching applications', error);
      }).finally(() => {
        this.isFetching = false;
      });
    },
    deletePromise() {
      return NiotaClassicApiService.applicationsDelete(this.deleteItemData);
    },
    viewApplication(applicationId) {
      this.$router.push({
        path: `/niota-classic/applications/${applicationId}/details`
      });
    },
    showDeletePrompt(item) {
      this.deletePrompt = true;
      this.deleteItemData = item;
    },
    deleteItem() {
      this.deletePrompt = false;
      this.deletePromise().then(() => {
        this.deleteItemData = null;
        this.$snack(this.$i18n_t('NiotaClassic.Applications.snacks.deleteSuccess'), 'success');
        this.fetchList(true);
      }).catch(error => {
        this.deleteItemData = null;
        this.$snack(get(error, 'response.data.err') || this.$i18n_t('NiotaClassic.Applications.snacks.deleteFailed'), 'error');
      });
    },
    getAccountIds(accounts) {
      let ids = [];
      accounts.forEach(account => {
        ids.push(account.id);
      });
      return ids;
    }
  }
};